/**
 * Settings.js — a tela Configurações
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * ONDA 100, FASE 1 — REORGANIZAR SEM MUDAR O QUE A TELA FAZ
 *
 * Esta tela é o painel de controle da plataforma e do motor de reconhecimento. Até
 * a Onda 99 ela era uma aba única com oito blocos e 62 campos abertos, empilhados
 * na ordem em que foram criados. Agora tem um menu lateral por FINALIDADE e mostra
 * uma seção por vez; cada campo é uma linha compacta, com a primeira frase da
 * explicação à vista e o resto atrás de "saiba mais".
 *
 * O QUE NÃO MUDOU: as chaves, a rota (`PUT /api/settings/:key`), os valores, as
 * conversões de unidade (ms ↔ horas), os limites de cada campo, o servidor. Os
 * dados saíram daqui para `data/settingsMetaBase.js` e `data/webhookPayloads.js`,
 * e os blocos com desenho próprio para `components/settings/` — por script, sem
 * redigitação. ONDE cada parâmetro aparece vive em `data/settingsEstrutura.js`.
 *
 * A ÚNICA MUDANÇA DE COMPORTAMENTO, e é uma correção: SAIR DE UM CAMPO SEM ALTERAR
 * NADA GRAVAVA O VALOR DE NOVO. Todo campo salvava ao perder o foco, sem comparar
 * com o que já estava gravado. Num campo do motor isso regravava o ambiente do
 * motor e o REINICIAVA — um minuto sem leituras por clicar num campo e sair dele.
 * `handleSave` agora compara com o último valor persistido e não faz nada quando é
 * igual. Campo esvaziado volta ao valor gravado em vez de mandar vazio ao servidor
 * (que respondia 400). E o aviso de sucesso passou a dizer quando o motor reinicia
 * — o servidor já informava (`fastalpr_restarted`), a tela é que ignorava.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import PrintingSettingsCard from '../components/PrintingSettingsCard';
import {
  BlocoModoDeFiltragem, BarraDoMotor, BlocoLimiares, BlocoLinhaDoTempo,
  BlocoSuperexposicao, BlocoProcessador,
} from '../components/settings/BlocosDeConfiguracao';
import { SETTINGS_META, reiniciaOMotor, resumoDa } from '../data/settingsMeta';
import { ESTRUTURA, todasAsSecoes, valeNaVertical, blocoDasChaves } from '../data/settingsEstrutura';
import { WEBHOOK_PAYLOADS } from '../data/webhookPayloads';
import { CONFIRMACOES, pedeConfirmacao } from '../data/settingsConfirmacoes';
import { buscarParametro } from '../data/settingsBusca';
import DialogoDeConfirmacao from '../components/settings/DialogoDeConfirmacao';
import BlocoTelegram from '../components/BlocoTelegram'; // Onda 117
import BlocoSemaforo from '../components/BlocoSemaforo'; // Onda 141
import '../styles/Settings.css';
import '../styles/Settings_extra.css';
import '../styles/SettingsNav.css';

// ─── Componente de Payload ───────────────────────────────────────────────────
const PayloadCard = ({ item }) => {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(item.payload, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const abre = item.payload.action === 'open';

  return (
    <div className="payload-card">
      <div className="payload-card-header">
        <div>
          <div className="payload-title">{item.title}</div>
          <div className="payload-desc">{item.desc}</div>
        </div>
        <span className={`payload-action-badge cfg-acao ${abre ? 'cfg-acao-abre' : 'cfg-acao-nega'}`}>
          {abre ? 'ABRE CANCELA' : 'NÃO ABRE'}
        </span>
      </div>
      <div className="payload-code-wrap">
        <button className="payload-copy-btn" onClick={handleCopy}>{copied ? 'Copiado!' : 'Copiar'}</button>
        <pre className="payload-code">{json}</pre>
      </div>
    </div>
  );
};

const br = (n) => String(n).replace('.', ',');

/** O número cabe nos limites que o próprio campo declara? */
const foraDaFaixa = (meta, texto) => {
  if (meta.min === undefined && meta.max === undefined) return false;
  const n = Number(String(texto).replace(',', '.'));
  return !Number.isFinite(n) || (meta.min !== undefined && n < meta.min) || (meta.max !== undefined && n > meta.max);
};

/** "aceita de 1 a 60 s" — a mesma frase serve à dica do campo e à recusa. */
const faixaDe = (meta) => {
  // "de 0 a 1 fração" não é português: fração e proporção não são unidade de medida.
  const un = meta.unit && !['fração', 'proporção'].includes(meta.unit) ? ` ${meta.unit}` : '';
  if (meta.min !== undefined && meta.max !== undefined) return `aceita de ${br(meta.min)} a ${br(meta.max)}${un}`;
  if (meta.min !== undefined) return `aceita ${br(meta.min)}${un} ou mais`;
  return `aceita até ${br(meta.max)}${un}`;
};

/**
 * Onda 101b — o Intervalo de Captura GERAL é só a reserva: vale para a câmera que não
 * tem intervalo próprio (`intervalo = camera.frame_interval || geral`, em rtspService).
 * No G2 as duas câmeras têm 1000 ms definidos no módulo Câmeras, e a tela mostrava
 * "2000 ms" sem dizer que aquilo não valia para nenhuma. O Iatagã perguntou por que
 * estava em 2000 se ele tinha configurado 1000 dias antes — a tela é que mentia por
 * omissão. Aqui ela diz o que está EM USO, câmera por câmera.
 */
const IntervaloEmUso = ({ cameras, geral }) => {
  if (!Array.isArray(cameras) || cameras.length === 0) return null;
  const ativas = cameras.filter(c => c.status !== 'inativa');
  if (ativas.length === 0) return null;
  const comProprio = ativas.filter(c => Number(c.frame_interval) > 0);
  const todasTemProprio = comProprio.length === ativas.length;
  return (
    <div className="cfg-em-uso">
      <p className="cfg-em-uso-titulo">Em uso agora, por câmera:</p>
      <ul>
        {ativas.map(c => {
          const proprio = Number(c.frame_interval) > 0;
          return (
            <li key={c.id}>
              <strong>{c.name}</strong>: {proprio ? c.frame_interval : (geral || '—')} ms
              <span className="cfg-em-uso-origem">{proprio ? ' — definido na câmera (módulo Câmeras)' : ' — usa este valor geral'}</span>
            </li>
          );
        })}
      </ul>
      <p className="cfg-em-uso-nota">
        {todasTemProprio
          ? 'Todas as câmeras têm intervalo próprio: este valor geral não está em uso por nenhuma. Para mudar o ritmo de captura, altere o intervalo no módulo Câmeras.'
          : 'O intervalo definido na câmera prevalece sobre este valor geral.'}
      </p>
    </div>
  );
};

/** A primeira frase à vista; o resto, a um clique. */
const Descricao = ({ texto }) => {
  const [aberta, setAberta] = useState(false);
  const { resumo, resto } = useMemo(() => resumoDa(texto), [texto]);
  if (!resumo) return null;
  return (
    <p className="cfg-desc">
      {resumo}
      {resto && aberta && <> {resto}</>}
      {resto && (
        <button type="button" className="cfg-mais" onClick={() => setAberta(a => !a)} aria-expanded={aberta}>
          {aberta ? 'mostrar menos' : 'saiba mais'}
        </button>
      )}
    </p>
  );
};

// ─── Componente Principal ────────────────────────────────────────────────────
const Settings = () => {
  const { vertical, cobranca, isSuperadmin } = useAuth();
  // Onda 154: o que a instalação tem além da vertical (cobrança) e quem está olhando (superadmin).
  const extra = useMemo(() => ({ cobranca, superadmin: isSuperadmin }), [cobranca, isSuperadmin]);
  const [settings, setSettings] = useState({});
  // O que está GRAVADO no servidor, por chave. `settings` muda a cada tecla; isto só
  // muda quando uma gravação dá certo. É contra ele que `handleSave` compara.
  const gravado = useRef({});
  const emVoo = useRef({});
  // Onda 101
  const [confirmando, setConfirmando] = useState(null);   // { key, de, para } — parâmetro de Operação à espera do "sim"
  const [pendentes, setPendentes] = useState({});          // { chave: valor novo } — alterações do motor ainda não aplicadas
  const [aplicando, setAplicando] = useState(null);        // null | { ocupado, erro } — o diálogo de aplicar
  const [erros, setErros] = useState({});                  // { chave: por que o último valor foi recusado }
  const [vigiaDeEntrada, setVigiaDeEntrada] = useState(null);   // o serviço `entry_flow` de /api/system/health
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [alprStatus, setAlprStatus] = useState(null);
  const [deviceSwitching, setDeviceSwitching] = useState(false); // true enquanto o FastALPR está reiniciando
  const [webhookTab, setWebhookTab] = useState('estacionamento');

  // A seção aberta mora na URL (?secao=passagem): dá para voltar, recarregar e
  // mandar o link de uma seção para alguém.
  const [params, setParams] = useSearchParams();
  const secoes = useMemo(() => todasAsSecoes().filter(s => valeNaVertical(s, vertical, extra)), [vertical, extra]);
  const secaoAtual = secoes.find(s => s.id === params.get('secao')) || secoes[0];
  const abrirSecao = (id) => setParams({ secao: id }, { replace: false });

  // ── Onda 104: a busca por parâmetro ────────────────────────────────────────
  const [busca, setBusca] = useState('');
  const [destaque, setDestaque] = useState(null);   // a chave para onde a busca acabou de levar
  const campoDeBusca = useRef(null);
  const buscando = busca.trim().length >= 2;

  /** Leva ao campo: abre a seção, e o efeito abaixo cuida de mostrar a linha. */
  const irParaParametro = (achado) => {
    setBusca('');
    setDestaque(achado.chave);
    if (achado.secaoId !== secaoAtual.id) abrirSecao(achado.secaoId);
  };

  // Depois que a seção está na tela: abre o grupo recolhido em que o campo mora, rola
  // até ele e deixa o destaque alguns segundos. Campo de bloco com desenho próprio não
  // tem linha (`data-chave`): aí a busca para no alto da seção, que já é o lugar certo.
  useEffect(() => {
    if (!destaque || buscando) return undefined;
    const linha = document.querySelector(`[data-chave="${destaque}"]`);
    if (linha) {
      const recolhido = linha.closest('details');
      if (recolhido) recolhido.open = true;
      if (typeof linha.scrollIntoView === 'function') linha.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    const t = setTimeout(() => setDestaque(null), 3500);
    return () => clearTimeout(t);
  }, [destaque, buscando, secaoAtual.id]);

  const achados = useMemo(
    () => (buscando ? buscarParametro(busca, { vertical, settings, ...extra }) : []),
    [buscando, busca, vertical, settings, extra]
  );

  // "/" põe o cursor na busca, como em toda ferramenta de quem vive no teclado — menos
  // quando já se está digitando em algum campo.
  useEffect(() => {
    const tecla = (e) => {
      const emCampo = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '') || e.target?.isContentEditable;
      if (e.key === '/' && !emCampo && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        campoDeBusca.current?.focus();
      }
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, []);

  // Busca status em tempo real do fastalpr-api via endpoint consolidado
  useEffect(() => {
    const fetchAlprStatus = async () => {
      try {
        const res = await api.get('/api/system/health');
        // Extrair dados do Motor ALPR da resposta consolidada
        // Onda 102: o vigia de entrada, para a seção dele mostrar o veredito de agora.
        setVigiaDeEntrada((res.data.services || []).find(s => s.service === 'entry_flow') || null);
        const alprService = (res.data.services || []).find(s => s.service === 'alpr');
        if (alprService) {
          const newStatus = {
            ready:                     alprService.status === 'online',
            uptime_seconds:            alprService.uptime,
            current_period:            alprService.current_period,
            current_threshold:         alprService.current_threshold,
            dynamic_threshold_enabled: alprService.dynamic_threshold_enabled,
            // Onda 5
            inference_device:          alprService.inference_device || 'cpu',
            cuda_available:            alprService.cuda_available   || false,
          };
          setAlprStatus(newStatus);
          // Se o serviço voltou (ready=true) após um switch, encerrar o estado de switching
          if (newStatus.ready) setDeviceSwitching(false);
        } else {
          setAlprStatus(null);
        }
      } catch { setAlprStatus(null); }
    };
    fetchAlprStatus();
    // Polling mais frequente (10s) quando está reiniciando, 60s no estado normal
    const interval = setInterval(fetchAlprStatus, deviceSwitching ? 10000 : 60000);
    return () => clearInterval(interval);
  }, [deviceSwitching]);

  // Altera o device de inferência (cpu ↔ gpu) via API
  const handleSwitchDevice = async (targetDevice) => {
    if (deviceSwitching) return;
    const label = targetDevice === 'gpu' ? 'GPU (CUDA)' : 'CPU';
    if (!window.confirm(`Deseja alterar o device de inferência para ${label}?\n\nO motor de reconhecimento será reiniciado. O sistema ficará indisponível por aproximadamente 30 segundos.`)) return;
    try {
      setDeviceSwitching(true);
      await api.post('/api/system/inference-device', { device: targetDevice });
      toast.success(`Alterando para ${label}... O motor está reiniciando.`);
    } catch (err) {
      setDeviceSwitching(false);
      toast.error(err?.response?.data?.error || 'Erro ao alterar device de inferência.');
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('/api/settings');
        const s = {};
        // A API retorna um objeto { key: { value, description } }
        // Suporta também o formato legado array [{ setting_key, setting_value }]
        if (Array.isArray(res.data.data)) {
          res.data.data.forEach(item => { s[item.setting_key] = item.setting_value; });
        } else if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
          Object.entries(res.data).forEach(([k, v]) => {
            s[k] = typeof v === 'object' && v !== null ? v.value : v;
          });
        }
        gravado.current = { ...s };
        setSettings(s);
      } catch (err) { toast.error('Erro ao carregar configurações'); }
      finally { setLoading(false); }
    };
    loadSettings();
  }, []);

  // Onda 101b: as câmeras, para a tela dizer que intervalo de captura está em uso de fato.
  // Se a lista não vier (perfil sem acesso, rede), a tela só deixa de mostrar o quadro.
  const [cameras, setCameras] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get('/api/cameras').then(r => { if (vivo) setCameras(Array.isArray(r.data) ? r.data : null); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  // Onda 26 (F-22): valores efetivos no motor (thresholds, modo de luminância, período atual)
  const [engineConfig, setEngineConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get('/api/settings/engine-config').then(r => { if (!cancelled) setEngineConfig(r.data); }).catch(() => { if (!cancelled) setEngineConfig(null); });
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  /** Devolve o campo ao que está gravado (ou ao padrão mostrado, se a chave não existe). */
  const restaurar = (key) => setSettings(prev => {
    const s = { ...prev };
    if (gravado.current[key] === undefined) delete s[key]; else s[key] = gravado.current[key];
    return s;
  });

  /**
   * Onda 101 — em campo do motor a pendência nasce ENQUANTO SE DIGITA, não ao sair do
   * campo. Na primeira versão só o `blur` registrava: o Iatagã digitou, ficou com o cursor
   * no campo, e a tela não disse nada — nem barra, nem aviso. Como pendência não manda
   * nada ao servidor, registrar a cada tecla não custa. O que ainda não é um valor
   * aceitável (vazio, fora da faixa) não entra na barra; a recusa, com a faixa, vem ao
   * sair do campo ou no Enter, em `handleSave`.
   */
  const aoDigitar = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    const meta = SETTINGS_META[key] || {};
    const novo = String(value);
    // A recusa aparece NA MESMA TECLA em que o valor deixa de servir. Sem isto, digitar
    // "9" mostrava a barra de pendências e o segundo "9" a fazia SUMIR EM SILÊNCIO — 99
    // está fora da faixa e não conta como pendência, mas o motivo só vinha ao sair do
    // campo. O Iatagã viu a barra sumir sem explicação e, com razão, chamou de bug.
    const naoServe = novo !== '' && meta.type === 'number' && !meta.disabled && foraDaFaixa(meta, novo);
    setErros(prev => {
      if (naoServe) return { ...prev, [key]: `"${novo}" está fora da faixa: ${faixaDe(meta)}.` };
      if (!prev[key]) return prev;
      const e = { ...prev }; delete e[key]; return e;
    });
    if (!reiniciaOMotor(key)) return;
    const anterior = gravado.current[key];
    const igual = anterior !== undefined && anterior !== null
      && (String(anterior) === novo || (meta.type === 'number' && novo !== '' && Number(anterior) === Number(novo)));
    const serve = novo !== '' && !(meta.type === 'number' && foraDaFaixa(meta, novo));
    setPendentes(prev => {
      const p = { ...prev };
      if (igual || !serve) delete p[key]; else p[key] = novo;
      return p;
    });
  };

  /** Enter confirma o campo: num campo numérico solto, sem formulário, Enter não fazia nada. */
  const confirmarNoEnter = (e) => { if (e.key === 'Enter') e.target.blur(); };

  /**
   * Onda 101 — a porta de entrada de TODA alteração. Decide entre três destinos:
   *   - chave do motor  → fica PENDENTE (barra no pé da tela; um reinício para todas);
   *   - chave que pede confirmação → abre o diálogo, e só grava no "sim";
   *   - o resto → grava na hora, como sempre.
   */
  const handleSave = (key, value) => {
    const meta = SETTINGS_META[key] || {};
    const novo = String(value);
    const anterior = gravado.current[key];

    // Campo esvaziado: o servidor recusa valor vazio, salvo nas chaves que PRECISAM
    // poder voltar a vazio (`webhook_url`, e as `podeVazio` da Onda 102). Nas demais, em
    // vez de mandar e ouvir 400, o campo volta ao que está gravado.
    if (novo.trim() === '' && key !== 'webhook_url') {
      if (!meta.podeVazio) { restaurar(key); return; }
      const limpo = meta.vazioVira !== undefined ? meta.vazioVira : '';
      const jaVazio = anterior === undefined || anterior === null || String(anterior).trim() === '' || String(anterior) === limpo;
      if (jaVazio) { restaurar(key); return; }
      return gravar(key, limpo);
    }

    // Onda 102: a regra própria do campo (JSON do ajuste por câmera, formato da versão).
    // O servidor aceitaria qualquer texto — e depois o ignoraria em silêncio.
    if (meta.validar) {
      const recusa = meta.validar(novo);
      if (recusa) {
        toast.error(`${meta.label}: ${recusa}`, { duration: 7000 });
        setErros(prev => ({ ...prev, [key]: `${recusa} Nada foi gravado: corrija ou apague o campo.` }));
        return;   // o texto FICA no campo: um JSON de 80 caracteres não se redigita por um erro de vírgula
      }
    }

    // Fora da faixa que o próprio campo declara: nem pendente, nem gravado.
    if (meta.type === 'number' && foraDaFaixa(meta, novo)) {
      const recusa = `"${novo}" não foi aceito: ${faixaDe(meta)}. O valor anterior foi mantido.`;
      toast.error(`${meta.label}: ${recusa}`, { duration: 6000 });
      setErros(prev => ({ ...prev, [key]: recusa }));   // e fica escrito na linha: aviso flutuante passa batido
      restaurar(key);
      setPendentes(prev => { const p = { ...prev }; delete p[key]; return p; });
      return;
    }

    // "0.6" e "0.60" são o mesmo valor: o slider devolve duas casas, o banco pode ter uma.
    const mesmoNumero = meta.type === 'number' && anterior !== '' && novo !== ''
      && Number.isFinite(Number(anterior)) && Number(anterior) === Number(novo);
    // Onda 102: chave que AINDA NÃO EXISTE no banco mostra o padrão do servidor (`padrao`).
    // Sair do campo sem alterar gravava esse padrão — o mesmo vício de "gravar o que não
    // mudou", num canto que a fase 1 não cobriu. O padrão mostrado conta como o valor
    // vigente: só vira gravação quando o operador de fato o muda.
    const semRegistro = anterior === undefined || anterior === null;
    const igualAoPadrao = semRegistro && meta.padrao !== undefined
      && (String(meta.padrao) === novo || (meta.type === 'number' && novo !== '' && Number(meta.padrao) === Number(novo)));
    const igualAoGravado = igualAoPadrao || (!semRegistro && (String(anterior) === novo || mesmoNumero));

    if (reiniciaOMotor(key)) {
      setSettings(prev => ({ ...prev, [key]: novo }));
      setPendentes(prev => {
        const p = { ...prev };
        if (igualAoGravado) delete p[key]; else p[key] = novo;   // voltou ao que era: deixa de ser pendência
        return p;
      });
      return;
    }

    // Onda 100: nada mudou → nada a gravar.
    if (igualAoGravado) return;

    if (pedeConfirmacao(key)) {
      // O controle NÃO muda enquanto a pergunta está aberta: um interruptor que já
      // mostra "desativado" atrás do diálogo diz que a coisa foi feita. Só `gravar`,
      // depois do "sim" e do servidor, muda o que a tela mostra.
      restaurar(key);
      setConfirmando({ key, de: anterior, para: novo });
      return;
    }
    return gravar(key, novo);
  };

  const gravar = async (key, novo) => {
    // O mesmo valor já está a caminho do servidor (segundo "sair do campo" antes de a
    // primeira gravação voltar): não manda de novo.
    if (emVoo.current[key] === novo) return false;

    emVoo.current[key] = novo;
    setSaving(true);
    try {
      const res = await api.put(`/api/settings/${key}`, { value: novo });
      gravado.current = { ...gravado.current, [key]: novo };
      setSettings(prev => ({ ...prev, [key]: novo }));
      if (res.data?.streams_restarted) {
        toast.success('Intervalo de captura atualizado. Câmeras reiniciadas com o novo intervalo.');
      } else if (res.data?.fastalpr_restarted) {
        toast.success('Configuração salva. O motor de reconhecimento está reiniciando: cerca de um minuto sem leituras.', { duration: 7000 });
      } else {
        toast.success('Configuração salva!');
      }
      return true;
    } catch (err) {
      // O servidor explica a recusa (Onda 101); sem explicação, a frase de sempre.
      toast.error(err?.response?.data?.error || 'Erro ao salvar configuração');
      return false;
    }
    finally { delete emVoo.current[key]; setSaving(false); }
  };

  // ── Onda 101: confirmação dos parâmetros de Operação ───────────────────────
  const cancelarConfirmacao = () => { if (confirmando) restaurar(confirmando.key); setConfirmando(null); };
  const confirmarAlteracao = async () => {
    const { key, para } = confirmando;
    const ok = await gravar(key, para);
    if (!ok) restaurar(key);
    setConfirmando(null);
  };

  // ── Onda 101: alterações pendentes do motor ────────────────────────────────
  const chavesPendentes = Object.keys(pendentes);

  const descartarPendentes = () => {
    setSettings(prev => {
      const s = { ...prev };
      for (const k of Object.keys(pendentes)) { if (gravado.current[k] === undefined) delete s[k]; else s[k] = gravado.current[k]; }
      return s;
    });
    setPendentes({});
    setAplicando(null);
  };

  const aplicarPendentes = async () => {
    setAplicando({ ocupado: true, erro: null });
    try {
      const res = await api.post('/api/settings/engine-batch', { values: pendentes });
      gravado.current = { ...gravado.current, ...pendentes };
      setPendentes({});
      setAplicando(null);
      toast.success(res.data?.fastalpr_restarted
        ? 'Alterações aplicadas. O motor de reconhecimento está reiniciando: cerca de um minuto sem leituras.'
        : 'Alterações aplicadas.', { duration: 7000 });
    } catch (err) {
      setAplicando({ ocupado: false, erro: err?.response?.data?.error || 'Não foi possível aplicar. Nada foi alterado.' });
    }
  };

  // Alteração pendente e a aba fechando: o navegador pergunta antes.
  useEffect(() => {
    if (!chavesPendentes.length) return undefined;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [chavesPendentes.length]);

  const comoTexto = (key, v) => {
    const meta = SETTINGS_META[key] || {};
    if (v === undefined || v === null || v === '') return '—';
    if (meta.type === 'toggle') return String(v) === 'true' ? 'ativado' : 'desativado';
    if (meta.options) return meta.options.find(o => o.value === String(v))?.label || String(v);
    return `${String(v)}${meta.unit ? ` ${meta.unit}` : ''}`;
  };

  const handleTestWebhook = async () => {
    const url = settings.webhook_url;
    if (!url) { toast.error('Informe a URL do webhook antes de testar'); return; }
    setWebhookTesting(true);
    try {
      // Onda 101: a tela esperava `success` na resposta, e o servidor nunca mandou esse
      // campo — o teste dizia "retornou erro: undefined" justamente quando dava certo.
      // A rota responde 200 quando o webhook aceitou e 502, com o motivo, quando não.
      const res = await api.post('/api/settings/webhook_test', { url: settings.webhook_url });
      toast.success(`Webhook respondeu com sucesso${res.data?.status_code ? ` (HTTP ${res.data.status_code})` : ''}.`);
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao conectar com o webhook'); }
    finally { setWebhookTesting(false); }
  };

  const formatValue = (key, value) => {
    const meta = SETTINGS_META[key];
    if (!meta || !value) return value;
    if (meta.unit === 'ms') {
      const ms = parseInt(value);
      if (ms >= 3600000) return `${(ms/3600000).toFixed(1)}h`;
      if (ms >= 60000) return `${Math.round(ms/60000)} min`;
      return `${ms/1000}s`;
    }
    return value;
  };

  /** O que o motor está usando agora para esta chave, ao lado do que está gravado. */
  const noMotor = (key, valorDaTela, comparar) => {
    if (!engineConfig || engineConfig[key] === undefined || engineConfig[key] === null) return null;
    const difere = comparar(engineConfig[key], valorDaTela);
    return { difere, valor: engineConfig[key] };
  };

  // Funcao comum, NAO componente: declarada aqui dentro, um componente ganharia
  // identidade nova a cada render e o campo perderia o foco a cada tecla.
  const linha = ({ chave, meta, controle, extra, desligada }) => (
    <div className={`cfg-linha${desligada ? ' cfg-linha-desligada field-disabled' : ''}${pendentes[chave] !== undefined ? ' cfg-linha-pendente' : ''}${destaque === chave ? ' cfg-linha-destaque' : ''}`} data-chave={chave}>
      <div className="cfg-linha-texto">
        <span className="cfg-rotulo">
          {meta.label}
          {reiniciaOMotor(chave) && !meta.disabled && <span className="cfg-selo" title="Gravar este campo reinicia o motor de reconhecimento: cerca de um minuto sem leituras">reinicia o motor</span>}
        </span>
        <Descricao texto={meta.description} />
        {extra}
        {erros[chave] && <p className="cfg-erro" role="alert">{erros[chave]}</p>}
        {pendentes[chave] !== undefined && (
          <p className="cfg-pendente-nota">Alterado, ainda não aplicado — gravado: {comoTexto(chave, gravado.current[chave])}</p>
        )}
      </div>
      <div className="cfg-linha-controle">{controle}</div>
    </div>
  );

  const renderField = (key) => {
    const meta = SETTINGS_META[key];
    if (!meta) return null;
    const value = settings[key] ?? meta.padrao ?? '';

    if (meta.type === 'toggle') {
      const isOn = value === 'true';
      const m = noMotor(key, isOn, (a, b) => String(a) !== String(b));
      return linha({
        chave: key, meta, controle: (
          <>
            <button
              className={`toggle-switch ${isOn ? 'on' : 'off'}`}
              onClick={() => handleSave(key, !isOn)}
              disabled={saving}
              aria-label={meta.label}
              aria-pressed={isOn}
            >
              <span className="toggle-knob" />
              <span className="toggle-text">{isOn ? 'ATIVADO' : 'DESATIVADO'}</span>
            </button>
            {m && <span className={`field-converted ${m.difere ? 'field-differs' : ''}`} title="Valor efetivo no motor agora">motor: {m.valor ? 'ativado' : 'desativado'}</span>}
          </>
        ),
      });
    }

    if (meta.type === 'select') {
      const m = noMotor(key, value, (a, b) => String(a) !== String(b));
      return linha({
        chave: key, meta, controle: (
          <>
            <select
              className="field-select"
              value={value}
              onChange={e => { setSettings(prev => ({ ...prev, [key]: e.target.value })); handleSave(key, e.target.value); }}
              disabled={saving}
              aria-label={meta.label}
            >
              {meta.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {m && <span className={`field-converted ${m.difere ? 'field-differs' : ''}`} title="Valor efetivo no motor agora">motor: {String(m.valor)}</span>}
          </>
        ),
      });
    }

    if (meta.type === 'url') {
      return (
        <div className="settings-field cfg-bloco-largo" data-chave={key}>
          <label className="field-label">{meta.label}</label>
          <p className="field-desc">{meta.description}</p>
          <div className="webhook-input-row">
            <input
              type="url"
              className="field-input"
              value={value}
              placeholder={typeof meta.placeholder === 'function' ? meta.placeholder(settings) : meta.placeholder}
              onChange={e => setSettings(prev => ({ ...prev, [key]: e.target.value }))}
              onBlur={e => handleSave(key, e.target.value)}
              onKeyDown={confirmarNoEnter}
            />
            <button className="btn-test-webhook" onClick={handleTestWebhook} disabled={webhookTesting || !value}>
              {webhookTesting ? 'Testando...' : 'Testar'}
            </button>
          </div>
          <details className="cfg-recolhido cfg-recolhido-interno">
            <summary>Exemplo de payload enviado</summary>
            <pre className="preview-code">{JSON.stringify({
              event: 'barrier_control', action: 'open', plate: 'ABC1D23',
              camera_type: 'entrada', vehicle_type: 'avulso',
              confidence: 0.97, timestamp: '2026-05-27T14:30:00.000Z', ticket_id: 42
            }, null, 2)}</pre>
          </details>
        </div>
      );
    }

    // Campos gravados em ms mas informados em horas (ex.: Janela de Abandono)
    if (meta.inputUnit === 'h') {
      const hours = value === '' ? '' : String(Number(value) / 3600000);
      const toMs = h => String(Math.round(Math.max(1, parseFloat(h) || 0) * 3600000));
      return linha({
        chave: key, meta,
          extra: meta.recommended && <p className="field-hint">Valor recomendado: {meta.recommended / 3600000} horas</p>,
          controle: (
            <>
              <input
                type="number" className="field-input" value={hours} min="1" step="1" disabled={saving} aria-label={meta.label}
                onChange={e => setSettings(prev => ({ ...prev, [key]: e.target.value === '' ? '' : toMs(e.target.value) }))}
                onBlur={e => handleSave(key, e.target.value === '' ? '' : toMs(e.target.value))}
                onKeyDown={confirmarNoEnter}
              />
              <span className="field-unit cfg-unidade">horas</span>
              {value && Number(value) >= 86400000 && (
                <span className="field-converted">= {(Number(value) / 86400000).toFixed(1).replace('.', ',')} dia(s)</span>
              )}
            </>
          ),
      });
    }

    const m = meta.type === 'text'
      ? noMotor(key, value, (a, b) => String(a) !== String(b))
      : noMotor(key, value, (a, b) => Math.abs(Number(a) - Number(b)) > 1e-6);
    const dica = meta.hint
      || (meta.recommended
        ? (meta.unit === 'ms'
          ? `Valor recomendado: ${formatValue(key, meta.recommended)} (${meta.recommended} ms)`
          : `Valor recomendado: ${String(meta.recommended).replace('.', ',')}${meta.unit && !['fração', 'proporção'].includes(meta.unit) ? ` ${meta.unit}` : ''}`)
        : null);
    // Borda vermelha enquanto o que ESTÁ no campo não serve — e só enquanto: depois que o
    // valor anterior volta, fica a frase da recusa, mas o campo já não está errado.
    const invalidoAgora = meta.type === 'number' && !meta.disabled && value !== '' && foraDaFaixa(meta, value);
    // A faixa aceita, à vista: quem vai digitar precisa saber ANTES de ser recusado.
    const faixa = meta.type === 'number' && !meta.disabled && (meta.min !== undefined || meta.max !== undefined)
      ? `${faixaDe(meta).replace(/^a/, 'A')}.` : null;
    return linha({
      chave: key, meta, desligada: meta.disabled,
        extra: (
          <>
            {(dica || faixa) && <p className="field-hint">{[dica, faixa].filter(Boolean).join(' · ')}</p>}
            {key === 'frame_interval' && <IntervaloEmUso cameras={cameras} geral={gravado.current.frame_interval} />}
          </>
        ),
        controle: (
          <>
            <input
              type={meta.type === 'time' ? 'time' : (meta.type === 'text' ? 'text' : 'number')}
              className={`field-input${meta.type === 'text' ? ' cfg-input-texto' : ''}${invalidoAgora ? ' cfg-input-invalido' : ''}`}
              aria-invalid={invalidoAgora ? 'true' : undefined}
              value={value}
              min={meta.min !== undefined ? String(meta.min) : undefined}
              max={meta.max !== undefined ? String(meta.max) : undefined}
              step={meta.step ? String(meta.step) : '1'}
              disabled={meta.disabled || saving}
              aria-label={meta.label}
              placeholder={typeof meta.placeholder === 'function' ? meta.placeholder(settings) : meta.placeholder}
              spellCheck={meta.type === 'text' ? false : undefined}
              onChange={e => !meta.disabled && aoDigitar(key, e.target.value)}
              onBlur={e => !meta.disabled && handleSave(key, e.target.value)}
              onKeyDown={confirmarNoEnter}
            />
            {/* Sem unidade, o espaço dela fica: é o que alinha todos os campos na mesma coluna. */}
            {meta.type !== 'text' && <span className="field-unit cfg-unidade">{meta.unit || ''}</span>}
            {value && meta.unit === 'ms' && (
              <span className="field-converted">= {formatValue(key, value)}</span>
            )}
            {m && <span className={`field-converted ${m.difere ? 'field-differs' : ''}`} title="Valor efetivo no motor agora">motor: {String(m.valor)}</span>}
          </>
        ),
    });
  };

  const renderBloco = (bloco) => {
    switch (bloco) {
      case 'impressao':
        return <PrintingSettingsCard />;
      case 'modoDeFiltragem':
        return <BlocoModoDeFiltragem settings={settings} handleSave={handleSave} saving={saving} />;
      case 'limiares':
        return <div className="cfg-cartao-unico"><BlocoLimiares settings={settings} setSettings={setSettings} handleSave={handleSave} saving={saving} engineConfig={engineConfig} /></div>;
      case 'linhaDoTempo':
        return <BlocoLinhaDoTempo />;
      case 'estadoDoVigia': {
        // O que o vigia está dizendo AGORA, ao lado dos números que o calibram: quem
        // ajusta uma régua precisa ver o que ela está medindo.
        if (!vigiaDeEntrada) return null;
        const tom = { online: 'ok', degraded: 'atencao', offline: 'perigo' }[vigiaDeEntrada.status] || 'neutro';
        const rotulo = { online: 'Normal', degraded: 'Entrada fraca', offline: 'Entrada muda', unknown: 'Sem veredito' }[vigiaDeEntrada.status] || vigiaDeEntrada.status;
        return (
          <div className={`cfg-vigia cfg-vigia-${tom}`} role="status">
            <span className="cfg-vigia-rotulo">Agora: {rotulo}</span>
            <span className="cfg-vigia-texto">{vigiaDeEntrada.message}</span>
            {vigiaDeEntrada.window_min != null && (
              <span className="cfg-vigia-numeros">
                Janela de {vigiaDeEntrada.window_min} min · {vigiaDeEntrada.entries} entrada(s) · {vigiaDeEntrada.exits} saída(s), {vigiaDeEntrada.orphan_exits} sem ticket · normal do horário: {vigiaDeEntrada.reference}
              </span>
            )}
            {vigiaDeEntrada.action && <span className="cfg-vigia-texto">{vigiaDeEntrada.action}</span>}
          </div>
        );
      }
      case 'telegram':
        // Onda 117: o bot, a escolha do grupo e a mensagem de teste.
        return <BlocoTelegram settings={settings} handleSave={handleSave} />;
      case 'semaforoBroker':
        // Onda 141: a senha do broker, a conexão e as controladoras vistas pelo heartbeat.
        return <BlocoSemaforo />;
      case 'superexposicao':
        return <div className="cfg-cartao-unico"><BlocoSuperexposicao settings={settings} /></div>;
      case 'processador':
        return <div className="cfg-cartao-unico"><BlocoProcessador alprStatus={alprStatus} deviceSwitching={deviceSwitching} handleSwitchDevice={handleSwitchDevice} /></div>;
      case 'referenciaDePayloads':
        return (
          <div className="webhook-reference">
            <div className="webhook-ref-intro">
              <p>
                Todos os payloads enviados pelo sistema via webhook estão documentados abaixo.
                Use esta referência para implementar o receptor de webhooks na sua aplicação de controle de cancelas.
                O campo <code>action</code> determina se a cancela deve ser aberta (<code>"open"</code>) ou bloqueada (<code>"deny"</code>).
              </p>
            </div>
            <div className="webhook-ref-tabs">
              <button className={`webhook-ref-tab ${webhookTab === 'estacionamento' ? 'active' : ''}`} onClick={() => setWebhookTab('estacionamento')}>
                Estacionamento Convencional
              </button>
              <button className={`webhook-ref-tab ${webhookTab === 'condominio' ? 'active' : ''}`} onClick={() => setWebhookTab('condominio')}>
                Condomínio Residencial / Corporativo
              </button>
            </div>
            <div className="payloads-list">
              {WEBHOOK_PAYLOADS[webhookTab].map((item, idx) => (
                <PayloadCard key={idx} item={item} />
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) return <div className="loading-state">Carregando configurações...</div>;

  const gruposVisiveis = secaoAtual.grupos.filter(g => valeNaVertical(g, vertical, extra) && (!g.visivelSe || g.visivelSe(settings)));
  const chavesDaSecao = secaoAtual.grupos.flatMap(g => g.itens.flatMap(i => (typeof i === 'string' ? [i] : blocoDasChaves[i.bloco] || [])));
  const secaoMexeNoMotor = chavesDaSecao.some(k => reiniciaOMotor(k) && !SETTINGS_META[k]?.disabled);

  const corpoDoGrupo = (g) => (
    <div className="cfg-grupo-corpo">
      {g.nota && <p className="cfg-nota">{g.nota}</p>}
      {g.itens.map((item) => (typeof item === 'string'
        ? <React.Fragment key={item}>{renderField(item)}</React.Fragment>
        : <React.Fragment key={item.bloco}>{renderBloco(item.bloco)}</React.Fragment>))}
    </div>
  );

  // A barra acompanha QUALQUER seção — e a busca também: pendência não some da tela
  // porque o operador foi procurar outro campo.
  const barraDePendencias = chavesPendentes.length > 0 && (
    <div className="cfg-pendencias" role="status">
      <span className="cfg-pendencias-texto">
        <strong>{chavesPendentes.length === 1 ? '1 alteração pendente' : `${chavesPendentes.length} alterações pendentes`} no motor.</strong>{' '}
        Nada foi gravado ainda. Aplicar reinicia o reconhecimento uma vez: cerca de um minuto sem leituras.
      </span>
      <button type="button" className="cfg-botao" onClick={descartarPendentes}>Descartar</button>
      <button type="button" className="cfg-botao cfg-botao-principal" onClick={() => setAplicando({ ocupado: false, erro: null })}>Revisar e aplicar</button>
    </div>
  );

  return (
    <div className="settings-page cfg-pagina">
      <div className="page-header">
        <div>
          <h1>Configurações</h1>
          <p className="page-subtitle">Parâmetros da plataforma e do motor de reconhecimento</p>
        </div>
        <div className="cfg-busca" role="search">
          <input
            ref={campoDeBusca}
            type="search"
            className="cfg-busca-campo"
            value={busca}
            placeholder="Buscar parâmetro…  ( / )"
            aria-label="Buscar parâmetro pelo nome"
            autoComplete="off"
            spellCheck={false}
            onChange={e => setBusca(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') { setBusca(''); e.target.blur(); }
              if (e.key === 'Enter' && achados.length > 0) irParaParametro(achados[0]);
            }}
          />
        </div>
      </div>

      <div className="cfg-corpo">
        <nav className="cfg-menu" aria-label="Seções das configurações">
          {ESTRUTURA.map(g => {
            const doGrupo = g.secoes.filter(s => valeNaVertical(s, vertical, extra));
            if (!doGrupo.length) return null;
            return (
              <div key={g.grupo} className="cfg-menu-grupo">
                <div className="cfg-menu-titulo">{g.grupo}</div>
                {doGrupo.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    className={`cfg-menu-item${s.id === secaoAtual.id ? ' cfg-menu-item-ativo' : ''}`}
                    aria-current={s.id === secaoAtual.id ? 'page' : undefined}
                    onClick={() => abrirSecao(s.id)}
                  >
                    {s.titulo}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        {buscando && (
          <section className="cfg-secao" aria-labelledby="cfg-busca-titulo">
            <header className="cfg-secao-cabeca">
              <h2 id="cfg-busca-titulo">Busca</h2>
              <p>
                {achados.length === 0
                  ? `Nenhum parâmetro com "${busca.trim()}". Tente um pedaço do nome (abandono, webhook, brilho) ou a chave técnica.`
                  : `${achados.length === 1 ? '1 parâmetro' : `${achados.length} parâmetros`} com "${busca.trim()}". Enter abre o primeiro; Esc limpa a busca.`}
              </p>
            </header>
            {achados.length > 0 && (
              <ul className="cfg-achados">
                {achados.map(a => (
                  <li key={a.chave}>
                    <button type="button" className="cfg-achado" onClick={() => irParaParametro(a)}>
                      <span className="cfg-achado-rotulo">
                        {a.rotulo}
                        {a.reiniciaOMotor && <span className="cfg-selo">reinicia o motor</span>}
                      </span>
                      <span className="cfg-achado-caminho">{a.caminho} · <code>{a.chave}</code></span>
                      {a.trecho && <span className="cfg-achado-trecho">{a.trecho}</span>}
                      {a.foraDeVista && <span className="cfg-achado-aviso">{a.foraDeVista}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {barraDePendencias}
          </section>
        )}

        {!buscando && (
        <section className="cfg-secao" key={secaoAtual.id} aria-labelledby="cfg-secao-titulo">
          {secaoAtual.grupo === 'Reconhecimento' && <BarraDoMotor alprStatus={alprStatus} />}

          <header className="cfg-secao-cabeca">
            <h2 id="cfg-secao-titulo">{secaoAtual.titulo}</h2>
            <p>{secaoAtual.desc}</p>
          </header>

          {secaoMexeNoMotor && (
            <p className="cfg-aviso-motor">
              Os campos com o selo <span className="cfg-selo">reinicia o motor</span> não gravam na hora: a alteração fica
              pendente, e você aplica todas juntas, com um reinício só (cerca de um minuto sem leituras). Depois, confira o
              efeito na tela Qualidade do OCR.
            </p>
          )}

          {gruposVisiveis.map((g, i) => (g.recolhido ? (
            <details key={g.titulo || i} className="cfg-grupo cfg-recolhido">
              <summary>{g.titulo}</summary>
              {corpoDoGrupo(g)}
            </details>
          ) : (
            <div key={g.titulo || i} className="cfg-grupo">
              {g.titulo && <h3 className="cfg-grupo-titulo">{g.titulo}</h3>}
              {corpoDoGrupo(g)}
            </div>
          )))}

          {barraDePendencias}
        </section>
        )}
      </div>

      {aplicando && (
        <DialogoDeConfirmacao
          titulo="Aplicar as alterações do motor?"
          rotuloConfirmar="Aplicar e reiniciar o motor"
          ocupado={aplicando.ocupado}
          erro={aplicando.erro}
          aoConfirmar={aplicarPendentes}
          aoCancelar={() => setAplicando(null)}
        >
          <p>O motor de reconhecimento será reiniciado <strong>uma vez</strong>: cerca de um minuto sem leituras nas câmeras. Prefira fora do pico.</p>
          <table className="cfg-dialogo-tabela">
            <thead><tr><th>Parâmetro</th><th>Gravado</th><th>Novo</th></tr></thead>
            <tbody>
              {chavesPendentes.map(k => (
                <tr key={k}>
                  <td>{SETTINGS_META[k]?.label || k}</td>
                  <td>{comoTexto(k, gravado.current[k])}</td>
                  <td><strong>{comoTexto(k, pendentes[k])}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="cfg-dialogo-nota">Ou todas entram, ou nenhuma: se o servidor recusar um valor, nada é alterado.</p>
        </DialogoDeConfirmacao>
      )}

      {confirmando && (() => {
        const c = CONFIRMACOES[confirmando.key](confirmando.de, confirmando.para);
        return (
          <DialogoDeConfirmacao
            titulo={c.titulo}
            rotuloConfirmar={c.rotulo}
            perigo={c.perigo}
            ocupado={saving}
            aoConfirmar={confirmarAlteracao}
            aoCancelar={cancelarConfirmacao}
          >
            {c.paragrafos.map((p, i) => <p key={i}>{p}</p>)}
          </DialogoDeConfirmacao>
        );
      })()}
    </div>
  );
};

export default Settings;
